import { logger } from '../../utils/logger'
import { useFetchApi } from '../../adapter/fetchApi'
import { documentAdapter } from '../../adapter/documentsAdapter'
import { Document, DocumentListResponse } from '../../domain/documents.types'
import { createTempBlobDownloadLink } from '../utils/reports'
import { setGetDocumentQueryParams } from './utils/useDocumentsApi.utils'

export const useDocumentsApi = () => {
  const fetchApi = useFetchApi()
  return {
    getDocumentListByUserWithCount: async (
      options: {
        pageIndex: number
        pageSize: number
      },
      search: string | null,
      sorting: { id: string; desc: boolean }[]
    ): Promise<DocumentListResponse> => {
      try {
        let url = `users/current/reports`
        const params = setGetDocumentQueryParams(options, search, sorting)

        if (params.toString()) {
          url += `?${params.toString()}`
        }

        const data = await fetchApi(url)

        const { count, reports } = data as { count: number; reports: unknown }

        if (!reports || !Array.isArray(reports)) {
          throw new Error(
            `usedocumentApi.getDocumentListByUser Wrong documents format : ${JSON.stringify(data)}`
          )
        }
        const mappedReports = reports.map(documentAdapter.toDomain)
        return {
          data: mappedReports,
          pageCount: Math.ceil(count / options.pageSize),
          rowCount: count,
        }
      } catch (error: any) {
        logger.error(error.message)
        return { data: [], pageCount: 0, rowCount: 0 }
      }
    },
    getDocumentListByPlantWithCount: async (
      plantId: number,
      options: {
        pageIndex: number
        pageSize: number
      },
      search: string | null,
      sorting: { id: string; desc: boolean }[]
    ): Promise<{ data: Document[]; pageCount: number; rowCount: number }> => {
      try {
        const { pageSize } = options || {}
        let url = `/centrales/${plantId}/reports`
        const params = setGetDocumentQueryParams(options, search, sorting)

        if (params.toString()) {
          url += `?${params.toString()}`
        }

        const data = await fetchApi(url)

        const { count, reports } = data as { count: number; reports: unknown }

        if (!reports || !Array.isArray(reports)) {
          throw new Error(
            `usedocumentApi.getDocumentListByPlant Wrong documents format : ${JSON.stringify(
              reports
            )}`
          )
        }

        const mappedReports = reports.map(documentAdapter.toDomain)
        return {
          data: mappedReports,
          pageCount: Math.ceil(count / pageSize),
          rowCount: count,
        }
      } catch (error: any) {
        logger.error(error.message)
        return { data: [], pageCount: 0, rowCount: 0 }
      }
    },
    getBlob: async (blobId: string): Promise<void> => {
      const url = `reports/${blobId}/download`
      const config = { responseType: 'blob', method: 'GET' }
      try {
        const data = await fetchApi<Blob>(url, config, 'blob')
        if (!data) {
          throw new Error(
            `usedocumentApi.getBlob Bad downloads data received : ${JSON.stringify(data)}`
          )
        }
        const file = new Blob([data], { type: 'application/pdf' })
        if (file.size === 0) {
          throw new Error('Empty PDF file received')
        }
        const fileURL = URL.createObjectURL(file)
        const newTab = window.open(fileURL, '_blank')
        if (!newTab) {
          throw new Error('Failed to open new tab for PDF')
        }
      } catch (error: any) {
        logger.error(error.message)
        throw error
      }
    },
    downloadFile: async (fileToDownload: Document): Promise<Blob> => {
      const url = `reports/${fileToDownload.sharepointId}/download`
      const config = { responseType: 'blob', method: 'GET' }
      try {
        const data = await fetchApi<Blob>(url, config, 'blob')
        if (!data) {
          throw new Error(
            `usedocumentApi.downloadFile Bad downloads data received : ${JSON.stringify(data)}`
          )
        }

        const file = new Blob([data])
        const fileURL = URL.createObjectURL(file)
        const link = document.createElement('a')
        link.href = fileURL
        link.setAttribute(
          'download',
          `${fileToDownload.powerPlantId} - ${fileToDownload.displayName}.${fileToDownload.documentType}`
        )
        document.body.appendChild(link)
        link.click()
        URL.revokeObjectURL(fileURL)
        return data
      } catch (error: any) {
        logger.error(error.message)
        throw error
      }
    },
    downloadFiles: async (fileIds: string[]): Promise<void> => {
      const url = `reports/pdf/zip`
      const config = { responseType: 'blob', method: 'POST', body: { reportIds: fileIds } }
      try {
        const data = await fetchApi<Blob>(url, config, 'blob')
        if (!data) {
          throw new Error(
            `usedocumentApi.downloadFiles Bad downloads data received : ${JSON.stringify(data)}`
          )
        }
        createTempBlobDownloadLink(data, `rapports.zip`)
      } catch (error: any) {
        logger.error(error.message)
        throw error
      }
    },
  }
}
